import { RadioGroup as HeroUIRadioGroup } from "@heroui/react";
import clsx from "clsx";
import { getKey } from "core/model/utils/strings";
import { useTranslations } from "translations";
import { Radio, RadioProps } from "./radio";

export type RadioGroupOption<
  T extends string | number | boolean = string | number,
> = Pick<
  RadioProps,
  "aria-label" | "isDisabled" | "isInvalid" | "description"
> & {
  label: string;
  value: T;
  subForm?: (props: {
    isDisabled?: boolean;
    size?: RadioProps["size"];
  }) => React.ReactNode;
};

type HeroUIRadioGroupProps = React.ComponentProps<typeof HeroUIRadioGroup>;
type Color = Extract<HeroUIRadioGroupProps["color"], "primary">;
type Size = Extract<HeroUIRadioGroupProps["size"], "sm" | "lg" | "md">;

export type RadioGroupProps<
  T extends string | number | boolean = string | number,
> = Omit<
  HeroUIRadioGroupProps,
  | "children"
  | "classNames"
  | "color"
  | "name"
  | "onChange"
  | "onValueChange"
  | "size"
  | "value"
> & {
  color?: Color;
  name: string;
  onChange?: (value: T) => void;
  onValueChange?: (value: T) => void;
  options?: RadioGroupOption<T>[];
  size?: Size;
  value?: T;
};

export function RadioGroup<
  T extends string | number | boolean = string | number,
>({
  onChange,
  onValueChange,
  options,
  size = "md",
  ...props
}: RadioGroupProps<T>) {
  const translations = useTranslations();
  // Handle value changes with proper type conversion
  const handleValueChange = (newValue: string) => {
    if (!options?.length) return;

    // Find the option with matching string value
    const option = options.find((opt) => String(opt.value) === newValue);
    if (option) {
      // Return the original typed value
      onValueChange?.(option.value);
      onChange?.(option.value);
    }
  };

  // Convert current value to string for HeroUI
  const stringValue =
    props.value !== undefined ? String(props.value) : undefined;

  return (
    <HeroUIRadioGroup
      {...props}
      classNames={{
        description: "text-gray-600",
        errorMessage: "text-danger-dark",
        label: clsx(
          "text-base font-semibold text-default-900 after:text-gray-900",
          {
            "text-sm": size === "sm" || size === "md",
          },
        ),
        wrapper: clsx("flex", {
          "flex-row gap-6": props.orientation === "horizontal",
        }),
      }}
      data-testid={props.name}
      value={stringValue}
      onValueChange={handleValueChange}
    >
      {options?.map((option, i) => {
        const { label, subForm, value: optionValue, ...radioRest } = option;
        const isSelected = !!(optionValue && props.value === optionValue);
        const subFormId = `${props.name}-${optionValue}-subform`;

        return (
          <div
            key={getKey(String(optionValue), i)}
            className="flex flex-col gap-3"
          >
            <Radio
              key={String(optionValue)}
              {...radioRest}
              isDisabled={
                Boolean(props.isDisabled) || Boolean(option.isDisabled)
              }
              isInvalid={props.isInvalid}
              name={props.name}
              size={size}
              value={String(optionValue)}
            >
              {label}
            </Radio>
            {subForm && (
              <>
                <div
                  className={clsx("mb-2 pl-6 transition-all duration-200", {
                    "hidden opacity-0": !isSelected,
                    "opacity-100": isSelected,
                  })}
                  id={subFormId}
                  role="group"
                >
                  {subForm({
                    isDisabled:
                      Boolean(props.isDisabled) ||
                      Boolean(option.isDisabled),
                    size,
                  })}
                </div>
                {/* TODO: Screen reader announcement that sub form has been collapsed, should be using a11y hook: https://recare.atlassian.net/browse/DEV-17121 */}
                <span aria-live="polite" className="sr-only">
                  {isSelected
                    ? translations.accessibility.subFormExpanded
                    : translations.accessibility.subFormCollapsed}
                </span>
              </>
            )}
          </div>
        );
      })}
    </HeroUIRadioGroup>
  );
}

RadioGroup.displayName = "RecareUI.RadioGroup";
